import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DatabaseConnectionType} from '../../../../../data-source/models/database-connection-type.type';
import {Connection} from '../../../../../main/modules/management/models/Connection.model';
import {ConnectionType} from '../../../../../main/modules/management/models/connection.type';
import {ConfigConnectionForm} from '../../../../models/config-connection-form.model';
import {ConnectionTables} from '../../../../models/connection-tables.model';
import {SqlServerConnection} from '../../../../../data-source/models/sql-server-connection.model';
import {OracleConnection} from '../../../../../data-source/models/oracle-connection.model';
import {DataSource} from "../../../../../data-source/models/data-source.modol";

@Component({
    selector: 'tart-connection-form',
    templateUrl: './connection-form.component.html',
    styleUrls: ['./connection-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectionFormComponent implements OnInit {
    @Output() validatorChange = new EventEmitter();
    @Output() valueChanges = new EventEmitter();
    @Output() selectedConnection = new EventEmitter<Partial<Connection>>();
    @Output() connectionTypeChanges = new EventEmitter<ConnectionType>();

    @Input() configuration: Partial<ConfigConnectionForm>;
    @Input() connections: Partial<Connection>[];
    @Input() dataSource: DataSource;

    dataBaseForm: UntypedFormGroup;
    dataBaseConnectionTypes: { name: string; value: DatabaseConnectionType }[] = [
        {name: 'Microsoft SQL Server', value: 'MicrosoftSQLServer'},
    ];
    connectionTypes: { name: string; value: ConnectionType }[] = [
        {name: 'اتصال های موجود', value: 'Selectable'},
        {name: 'اتصال جدید', value: 'Queryable'},
    ];
    oracleConnectionTypes: { name: string; value: string }[] = [
        {name: 'شناسه سرویس', value: 'ServiceId'},
        {name: 'نام سرویس', value: 'ServiceName'},
    ];
    connectionType: ConnectionType = 'Queryable';
    _connection: Partial<Connection | SqlServerConnection | OracleConnection>;
    _connectionTables: Partial<ConnectionTables> = null;
    loading = false;
    showTables: { [p: string]: string };
    warning = false;

    constructor(private formBuilder: UntypedFormBuilder) {
    }

    get connection(): Partial<Connection | SqlServerConnection | OracleConnection> {
        return this._connection;
    }

    get connectionTables(): Partial<ConnectionTables> {
        return this._connectionTables;
    }

    @Input() set connection(connection: Partial<Connection | SqlServerConnection | OracleConnection>) {
        if(connection) {
            this._connection = connection;
            if(this.configuration.formType === 'EditConnection') {
                this.initialFormForEditConnection(connection);
            } else if(this.configuration.formType === 'EditSqlServer') {
                this.initialFormForEditSqlServer(connection);
            } else if(this.configuration.formType === 'EditOracle') {
                this.initialFormForEditOracle(connection);
            }
        }
    };

    @Input() set connectionTables(connectionTables: Partial<ConnectionTables>) {
        this.loading = false;
        this.showTables = null;
        this._connectionTables = connectionTables;
        if(this.dataBaseForm) {
            if(connectionTables == null) {
                this.dataBaseForm.controls.selectedConnectionTable.setValue('');
                this.dataBaseForm.controls.selectedConnectionTable.disable();
            }
            if(connectionTables != null) {
                this.showTables = {...connectionTables.tables};
                this.dataBaseForm.controls.selectedConnectionTable.enable();
                this.dataBaseForm.controls.selectedConnectionTable.setValidators(Validators.required);
                if (this.configuration.formType === 'EditSqlServer') {
                    const connection = this.connection as Partial<SqlServerConnection>;
                    this.dataBaseForm.controls.selectedConnectionTable.setValue(connection.tableName);

                    this.toggleRowLimited(connection.hasRowLimited);
                    if (connection.numberOfRowLimited) {
                        this.dataBaseForm.controls.numberOfRowLimited.setValue(connection.numberOfRowLimited);
                    }

                    this.toggleTopN(connection.hasTopN);
                    if (connection.numberOfTopN) {
                        this.dataBaseForm.controls.numberOfTopN.setValue(connection.numberOfTopN);
                    }
                }
            }
        }
    } ;

    ngOnInit(): void {
        this.initialConfig();
        this.emitValidator();
        this.dataBaseForm.valueChanges.subscribe(res=>{
          this.valueChanges.emit(res);
        });
    }

    emitValidator(): void {
        this.dataBaseForm.statusChanges.subscribe(status => {
            if (status === 'INVALID') {
                this.validatorChange.emit(null);
            } else if((this.connectionType === 'Selectable' && this.showTables != null) ||
                this.connectionType === 'Queryable'){
                this.validatorChange.emit(this.dataBaseForm.value);
            }
        });
    }

    changeConnectionType(type: ConnectionType): void {
        this.connectionType = type;
        if (this.configuration.formType === 'CreateSqlServer' ||
            this.configuration.formType === 'EditSqlServer' ||
            this.configuration.formType === 'ReplaceSqlServer') {

            if(this.configuration.formType !== 'EditSqlServer'){
              this.selectedConnection.emit(null);
            }
            this.dataBaseForm.controls.connectionType.setValue(type);
            if (type === 'Queryable') {
                this.setValidatorRequiredForQueryableConnection();
            } else if (type === 'Selectable') {
                this.setValidatorsRequiredForSelectableConnection();
            }
        }
    }

    toggleWindowsAuth(windowsAuth: boolean): void {
        this.dataBaseForm.controls.windowsAuth.setValue(windowsAuth);
        this.conditionalUsernameAndPasswordValidator(this.dataBaseForm.value.windowsAuth);
    }

    conditionalUsernameAndPasswordValidator(windowsAuth: boolean): void {
        if (windowsAuth === true) {
            this.dataBaseForm.controls.username.setValue('');
            this.dataBaseForm.controls.username.disable();
            this.dataBaseForm.controls.password.setValue('');
            this.dataBaseForm.controls.password.disable();
        } else if (windowsAuth === false) {
            this.dataBaseForm.controls.username.enable();
            this.dataBaseForm.controls.username.setValidators(Validators.required);
            this.dataBaseForm.controls.password.enable();
            this.dataBaseForm.controls.password.setValidators(Validators.required);
        }
    }

    toggleRowLimited(limited: boolean): void {
        this.dataBaseForm.controls.hasRowLimited.setValue(limited);
        if (limited) {
            this.dataBaseForm.controls.numberOfRowLimited.enable();
        } else {
            this.dataBaseForm.controls.numberOfRowLimited.setValue('');
            this.dataBaseForm.controls.numberOfRowLimited.disable();
        }
    }

    toggleTopN(limited: boolean): void {
        this.dataBaseForm.controls.hasTopN.setValue(limited);
        if (limited) {
            this.dataBaseForm.controls.numberOfTopN.enable();
        } else {
            this.dataBaseForm.controls.numberOfTopN.setValue('');
            this.dataBaseForm.controls.numberOfTopN.disable();
        }
        if(this.dataSource?.isIncremental){
          this.dataBaseForm.controls.hasTopN.disable();
          this.dataBaseForm.controls.numberOfTopN.disable();
        }
    }

    searchTable(searchKey: string): void {
        this.showTables = {};
        for (const tablesKey in this._connectionTables.tables) {
            if(tablesKey.toLowerCase().includes(searchKey.toLowerCase())) {
                this.showTables[tablesKey] = this._connectionTables.tables[tablesKey];
            }
        }
    }

    changeSelectedConnection(connection: Partial<Connection>): void {
        this.loading = true;
        this.selectedConnection.emit(connection);
        this.warning = false;
    }

    private initialConfig(): void {
        if (this.configuration.formType === 'CreateConnection' || this.configuration.formType === 'EditConnection') {
            this.dataBaseForm = this.formBuilder.group({
                name: ['', Validators.required],
                dbConnectionType: ['', Validators.required],
                serverAddress: ['', Validators.required],
                portNumber: ['', Validators.required],
                dbName: ['', Validators.required],
                windowsAuth: [false, Validators.required],
                username: ['', Validators.required],
                password: ['', Validators.required],
            });
        } else if (this.configuration.formType === 'CreateSqlServer' ||
            this.configuration.formType === 'EditSqlServer' ||
            this.configuration.formType === 'ReplaceSqlServer') {

            this.dataBaseForm = this.formBuilder.group({
                name: ['', Validators.required],
                connectionType: [{value: 'Queryable', disabled: false}, Validators.required],
                selectedConnection: [{value: '', disabled: true}],
                selectedConnectionTable: [{value: '', disabled: true}],
                hasRowLimited:[false],
                numberOfRowLimited: [{ value: '', disabled: true }, Validators.required],
                hasTopN: [false],
                numberOfTopN: [{ value: '', disabled: true }, Validators.required],
                serverAddress: [''],
                portNumber: [''],
                dbName: [''],
                windowsAuth: [false],
                username: [''],
                password: [''],
                commandTimeout: [''],
                connectionStatement: [''],
            });
            this.setValidatorRequiredForQueryableConnection();
        } else if (this.configuration.formType === 'CreateOracle' || this.configuration.formType === 'EditOracle') {
            this.dataBaseForm = this.formBuilder.group({
                name: ['', Validators.required],
                connectionType: [{value: 'Queryable', disabled: true}, Validators.required],
                selectedConnection: [''],
                selectedConnectionTable: [''],
                serverAddress: [''],
                portNumber: [''],
                windowsAuth: [false],
                serviceIdentity:[''],
                oracleConnectionType: ['ServiceId'],
                username: [''],
                password: [''],
                commandTimeout: [''],
                connectionStatement: [''],
            });
            this.setValidatorRequiredForQueryableConnection();
        } else {
            this.dataBaseForm = this.formBuilder.group({});
        }
    }

    private setValidatorRequiredForQueryableConnection(): void {
        this.dataBaseForm.controls.serverAddress.enable();
        this.dataBaseForm.controls.serverAddress.setValidators(Validators.required);
        this.dataBaseForm.controls.portNumber.enable();
        this.dataBaseForm.controls.portNumber.setValidators(Validators.required);
        this.dataBaseForm.controls.windowsAuth.enable();
        this.dataBaseForm.controls.windowsAuth.setValidators(Validators.required);
        this.dataBaseForm.controls.commandTimeout.enable();
        this.dataBaseForm.controls.commandTimeout.setValidators(Validators.required);
        this.dataBaseForm.controls.connectionStatement.enable();
        this.dataBaseForm.controls.connectionStatement.setValidators(Validators.required);
        if(this.configuration.formType !== 'EditSqlServer'){
          this.dataBaseForm.controls.selectedConnection.setValue('');
        }
        this.dataBaseForm.controls.selectedConnection.disable();
        if(this.configuration.formType !== 'EditSqlServer') {
          this.dataBaseForm.controls.selectedConnectionTable.setValue('');
        }
        this.dataBaseForm.controls.selectedConnectionTable.disable();
        if (this.configuration.formType === 'CreateSqlServer' ||
            this.configuration.formType === 'EditSqlServer' ||
            this.configuration.formType === 'ReplaceSqlServer') {
            this.dataBaseForm.controls.dbName.enable();
            this.dataBaseForm.controls.dbName.setValidators(Validators.required);
        } else if (this.configuration.formType === 'CreateOracle' || this.configuration.formType === 'EditOracle') {
            this.dataBaseForm.controls.serviceIdentity.setValue('');
            this.dataBaseForm.controls.serviceIdentity.enable();
            this.dataBaseForm.controls.oracleConnectionType.setValue('ServiceId');
            this.dataBaseForm.controls.oracleConnectionType.enable();
        }
        this.conditionalUsernameAndPasswordValidator(this.dataBaseForm.value.windowsAuth);
    }

    private setValidatorsRequiredForSelectableConnection(): void {
      if(this.configuration.formType !== 'EditSqlServer'){
        this.dataBaseForm.controls.serverAddress.setValue('');
        this.dataBaseForm.controls.portNumber.setValue('');
        this.dataBaseForm.controls.dbName.setValue('');
        this.dataBaseForm.controls.windowsAuth.setValue(false);
        this.dataBaseForm.controls.commandTimeout.setValue('');
        this.dataBaseForm.controls.username.setValue('');
        this.dataBaseForm.controls.connectionStatement.setValue('');
        this.dataBaseForm.controls.password.setValue('');
      }
      this.dataBaseForm.controls.serverAddress.disable();
      this.dataBaseForm.controls.portNumber.disable();
      this.dataBaseForm.controls.dbName.disable();
      this.dataBaseForm.controls.windowsAuth.disable();
      this.dataBaseForm.controls.commandTimeout.disable();
      this.dataBaseForm.controls.connectionStatement.disable();
      this.dataBaseForm.controls.username.disable();
      this.dataBaseForm.controls.password.disable();
      this.dataBaseForm.controls.selectedConnection.enable();
      this.dataBaseForm.controls.selectedConnection.setValidators(Validators.required);
      this.dataBaseForm.controls.selectedConnectionTable.enable();
      this.dataBaseForm.controls.selectedConnectionTable.setValidators(Validators.required);
    }

    private initialFormForEditConnection(connection: Partial<Connection>): void {
        this.setBaseForm(connection);
        this.dataBaseForm.controls.dbConnectionType.setValue('MicrosoftSQLServer');
        this.dataBaseForm.controls.dbName.setValue(connection.dbName);
        this.dataBaseForm.controls.windowsAuth.setValue(connection.username == null && connection.password == null);
        if(connection.username == null && connection.password == null) {
            this.dataBaseForm.controls.username.disable();
            this.dataBaseForm.controls.password.disable();
        } else {
            this.dataBaseForm.controls.username.setValue(connection.username);
            this.dataBaseForm.controls.password.setValue(connection.password);
        }
        this.dataBaseForm.updateValueAndValidity();
    }

    private initialFormForEditSqlServer(connection: Partial<SqlServerConnection>): void {
        this.setBaseForm(connection);
        this.dataBaseForm.controls.connectionType.setValue(connection.connectionType ?? 'Queryable');
        this.changeConnectionType(connection.connectionType);
        if(connection.connectionType === 'Selectable') {
            this.dataBaseForm.controls.selectedConnection.setValue(connection.dbConnectionId);
            this.warning = connection.dbConnectionId == null;
        } else {
            this.dataBaseForm.controls.commandTimeout.setValue(connection.commandTimeout);
            this.dataBaseForm.controls.connectionStatement.setValue(connection.sqlStatement);
            this.dataBaseForm.controls.windowsAuth.setValue(connection.windowsAuth);
            this.dataBaseForm.controls.dbName.setValue(connection.dbName);
            if (!connection.windowsAuth) {
                this.dataBaseForm.controls.username.setValue(connection.username);
                this.dataBaseForm.controls.password.setValue(connection.password);
            }
            this.conditionalUsernameAndPasswordValidator(connection.windowsAuth);
        }
    }

    private initialFormForEditOracle(connection: Partial<OracleConnection>): void {
        this.setBaseForm(connection);
        this.dataBaseForm.controls.commandTimeout.setValue(connection.commandTimeout);
        this.dataBaseForm.controls.connectionStatement.setValue(connection.sqlStatement);
        this.dataBaseForm.controls.windowsAuth
            .setValue(connection.oracleAuthenticationType === 'OperatingSystemAuthentication');
        if(connection.oracleAuthenticationType !== 'OperatingSystemAuthentication') {
            this.dataBaseForm.controls.username.setValue(connection.username);
            this.dataBaseForm.controls.password.setValue(connection.password);
        }
        this.dataBaseForm.controls.serviceIdentity.setValue(connection.serviceIdentity);
        this.dataBaseForm.controls.oracleConnectionType.setValue(connection.oracleConnectionType);
        this.conditionalUsernameAndPasswordValidator(
            connection.oracleAuthenticationType === 'OperatingSystemAuthentication',
        );
    }

    private setBaseForm(connection: Partial<Connection | SqlServerConnection | OracleConnection>): void {
        this.dataBaseForm.controls.name.setValue(connection.name);
        this.dataBaseForm.controls.serverAddress.setValue(connection.serverAddress);
        this.dataBaseForm.controls.portNumber.setValue(connection.portNumber);
    }
}
